/* eslint-disable no-confusing-arrow */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EditOsrContainer,
  EditOsrContent,
  EditOsrText,
  EditOsrButton,
  EditOsrTextSkeleton,
  EditOsrButtonSkeleton,
  EmailPopupOverlay,
  EmailPopupContainer,
  EmailPopupTitle,
  EmailPopupBody,
  EmailPopupInput,
  EmailPopupError,
  EmailPopupButtonContainer,
  EmailPopupCloseButton,
  EmailPopupSubmitButton,
} from './EditOsr.styles';
import { useBreakpoint } from 'hooks';
import { isEmail } from 'utils/validator';

const isProd = process.env.REACT_APP_ENV === 'prod';
const getCropwiseLoginUrl = () =>
  isProd
    ? 'https://accounts.cropwise.com/cw/cropwise_users/sign_in'
    : 'https://staging.accounts.cropwise.com/cw/cropwise_users/sign_in';
const getCropwiseSignupUrl = () =>
  isProd
    ? 'https://accounts.cropwise.com/cw/cropwise_users/sign_up/register'
    : 'https://staging.accounts.cropwise.com/cw/cropwise_users/sign_up/register';

const LOGIN_SOURCE = 'OSR-LOGIN';
const SIGNUP_SOURCE = 'OSR-SIGNUP';

interface EditOsrProps {
  CropwiseUser?: boolean | null;
  isDDG?: boolean;
  hasGrowerEmail?: boolean;
  growerEmail?: string | null;
  onSubmitEmail?: (email: string) => Promise<boolean> | void;
  onSaveGrowerEmail?: (email: string) => Promise<void> | void;
}

export const EditOsr: React.FC<EditOsrProps> = ({
  CropwiseUser,
  isDDG,
  hasGrowerEmail = true,
  growerEmail,
  onSubmitEmail,
  onSaveGrowerEmail,
}) => {
  const { isMobile } = useBreakpoint();

  const [t] = useTranslation();
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Skeletons show only while we're actively waiting on the Cropwise check for a
  // known grower email. When there's no grower email, we skip straight to the
  // button so the user can supply one via the popup instead.
  const isCropwiseStatusResolved = typeof CropwiseUser === 'boolean';
  const isCheckingCropwiseStatus = hasGrowerEmail && !isCropwiseStatusResolved;

  // This component (and its skeleton) is only relevant for non-DDG recommendations.
  if (isDDG) {
    return null;
  }

  const goToCropwiseUrl = (isCropwiseUser: boolean | null | undefined, email?: string | null) => {
    // Existing Cropwise users go to the Seller 2.0 login flow; new users go to
    // Cropwise account signup. Seller 2.0 uses the source param to route the
    // grower to the right page (source=OSR-SIGNUP lands on its signup page).
    const url = new URL(isCropwiseUser ? getCropwiseLoginUrl() : getCropwiseSignupUrl());
    if (!isProd) {
      url.searchParams.set('source', isCropwiseUser ? LOGIN_SOURCE : SIGNUP_SOURCE);
    }

    if (isCropwiseUser) {
      url.searchParams.set('show_password', 'true');
    }

    const trimmedEmail = email?.trim();
    if (trimmedEmail) {
      // Prefills the grower's email on the Cropwise page so they don't have to
      // retype it: login_hint on the sign-in form, email on the sign-up form.
      url.searchParams.set(isCropwiseUser ? 'login_hint' : 'email', trimmedEmail);
    }

    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const handleEditClick = () => {
    if (!hasGrowerEmail) {
      setEmailInput('');
      setEmailError('');
      setShowEmailPopup(true);
      return;
    }
    goToCropwiseUrl(CropwiseUser, growerEmail);
  };

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value);
    setEmailError('');
  };

  const handlePopupClose = () => {
    setShowEmailPopup(false);
    setEmailError('');
  };

  const handlePopupSubmit = async () => {
    const trimmedEmail = emailInput.trim();
    if (!trimmedEmail) {
      setEmailError(t('Email is required'));
      return;
    }
    if (!isEmail(trimmedEmail)) {
      setEmailError(t('Please enter a valid email address'));
      return;
    }

    setIsSubmitting(true);
    try {
      const isCropwiseUser = (await onSubmitEmail?.(trimmedEmail)) ?? undefined;
      // Save is best-effort: navigation shouldn't block on it, and a save failure
      // shouldn't stop the grower from proceeding to Cropwise.
      onSaveGrowerEmail?.(trimmedEmail)?.catch((error) => {
        console.error('Failed to save grower email:', error);
      });
      setShowEmailPopup(false);
      goToCropwiseUrl(isCropwiseUser, trimmedEmail);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <EditOsrContainer data-testid="edit-osr">
      <EditOsrContent>
        {isCheckingCropwiseStatus ? (
          <>
            <EditOsrTextSkeleton data-testid="edit-osr-text-skeleton" />
            <EditOsrButtonSkeleton data-testid="edit-osr-button-skeleton" />
          </>
        ) : (
          <>
            <EditOsrText>
              {t(
                'Want to change something in the recommendation? Click the button and make the edits yourself.'
              )}
            </EditOsrText>
            <EditOsrButton
              data-testid="edit-osr-button"
              onClick={handleEditClick}
              $isMobile={isMobile}
            >
              {isMobile ? t('Edit') : t('Edit Recommendation')}
            </EditOsrButton>
          </>
        )}
      </EditOsrContent>
      {showEmailPopup && (
        <>
          <EmailPopupOverlay data-testid="edit-osr-email-overlay" />
          <EmailPopupContainer data-testid="edit-osr-email-popup">
            <EmailPopupTitle>{t('Add Grower Email')}</EmailPopupTitle>
            <EmailPopupBody>
              {t('Grower’s email is not available. Please enter it to continue.')}
            </EmailPopupBody>
            <EmailPopupInput
              type="email"
              value={emailInput}
              onChange={handleEmailInputChange}
              $hasError={!!emailError}
              placeholder={t('Enter email address')}
              data-testid="edit-osr-email-input"
            />
            {emailError && <EmailPopupError>{emailError}</EmailPopupError>}
            <EmailPopupButtonContainer>
              <EmailPopupCloseButton
                data-testid="edit-osr-email-close"
                onClick={handlePopupClose}
                disabled={isSubmitting}
              >
                {t('Close')}
              </EmailPopupCloseButton>
              <EmailPopupSubmitButton
                data-testid="edit-osr-email-submit"
                onClick={handlePopupSubmit}
                disabled={isSubmitting || !isEmail(emailInput.trim())}
              >
                {t('Edit Recommendation')}
              </EmailPopupSubmitButton>
            </EmailPopupButtonContainer>
          </EmailPopupContainer>
        </>
      )}
    </EditOsrContainer>
  );
};
